import React, { useState } from 'react';
import {
  Drawer,
  Form,
  Button,
  DatePicker,
  SelectPicker,
  Message,
  InputNumber,
  Toggle
} from 'rsuite';
import { useDispatch } from 'react-redux';
import { format, isAfter, isBefore } from 'date-fns';
import { addSetting } from '@/services/feature.service';
import { useTranslation } from 'react-i18next';
interface AddHolidayDrawerProps {
  open: boolean;
  onClose: () => void;
  toaster: any;
}

const AddHolidayDrawer: React.FC<AddHolidayDrawerProps> = ({ open, onClose, toaster }) => {
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({
    name: 'Holidays',
    label: '',
    type: '',
    date_debut: null,
    date_fin: null,
    islamic: false,
    reccurent: false,
    daysPerYear: null
  });
  const [formError, setFormError] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();
  const typeOptions = [
    { label: t('PAID'), value: 'paid' },
    { label: t('UNPAID'), value: 'unpaid' },
    { label: t('RELIGIOUS'), value: 'religious' },
    { label: t('NATIONAL'), value: 'national' }
  ];

  const validateForm = () => {
    const errors = {};

    // Check required fields
    if (!formValue.label) {
      errors.label = 'Holiday name is required';
    }

    if (!formValue.type) {
      errors.type = 'Holiday type is required';
    }

    if (!formValue.date_debut) {
      errors.date_debut = 'Start date is required';
    }

    if (!formValue.date_fin) {
      errors.date_fin = 'End date is required';
    }

    // Check date logic if dates exist
    if (formValue.date_debut && formValue.date_fin) {
      if (isAfter(formValue.date_debut, formValue.date_fin)) {
        errors.date_fin = 'End date must be after start date';
      }
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      // Show toast for each error
      Object.keys(errors).forEach(key => {
        toaster.push(
          <Message type="error" duration={3000}>
            {errors[key]}
          </Message>,
          { placement: 'topCenter' }
        );
      });
      
      setFormError(errors);
      return;
    }

    setSubmitting(true);

    try {
      await dispatch(
        addSetting(
          {
            ...formValue,
            date_debut: format(formValue.date_debut, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
            date_fin: format(formValue.date_fin, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
            grades: []
          },
          'Holidays'
        )
      );
      
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Holiday added successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );

      // Reset form
      setFormValue({
        name: 'Holidays',
        label: '',
        type: '',
        date_debut: null,
        date_fin: null,
        islamic: false,
        reccurent: false,
        daysPerYear: null
      });
      
      onClose();
    } catch (error) {
      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error adding holiday:')} {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartDateChange = (date) => {
    setFormValue(prev => {
      // If end date is before new start date, update end date
      const updatedEndDate = prev.date_fin && isAfter(prev.date_fin, date) ? prev.date_fin : date;

      return {
        ...prev,
        date_debut: date,
        date_fin: updatedEndDate
      };
    });
  };

  const handleEndDateChange = (date) => {
    setFormValue(prev => {
      // Prevent setting end date before start date
      if (prev.date_debut && isAfter(prev.date_debut, date)) {
        return prev;
      }
      return {
        ...prev,
        date_fin: date
      };
    });
  };

  const handleToggleChange = (name: string, value: boolean) => {
    setFormValue(prev => {
      if (name === 'islamic') {
        return {
          ...prev,
          islamic: value,
          reccurent: value ? false : prev.reccurent,
          daysPerYear: value ? prev.daysPerYear : null
        };
      }
      if (name === 'reccurent') {
        return {
          ...prev,
          reccurent: value,
          islamic: value ? false : prev.islamic
        };
      }
      return prev;
    });
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Add New Holiday')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form
          fluid
          formValue={formValue}
          onChange={value => setFormValue(value)}
          formError={formError}
        >
          <Form.Group>
            <Form.ControlLabel>{t('Start Date')}</Form.ControlLabel>
            <DatePicker
              block
              format="yyyy-MM-dd"
              value={formValue.date_debut}
              onChange={handleStartDateChange}
              onSelect={handleStartDateChange}
              disabledDate={date => formValue.date_fin && isAfter(date, formValue.date_fin)}
              placement="auto"
            />
            {formError.date_debut && <Form.ErrorMessage>{formError.date_debut}</Form.ErrorMessage>}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('End Date')}</Form.ControlLabel>
            <DatePicker
              block
              format="yyyy-MM-dd"
              value={formValue.date_fin}
              onChange={handleEndDateChange}
              onSelect={handleEndDateChange}
              disabledDate={date => formValue.date_debut && isBefore(date, formValue.date_debut)}
              placement="auto"
            />
            {formError.date_fin && <Form.ErrorMessage>{formError.date_fin}</Form.ErrorMessage>}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Holiday Name')}</Form.ControlLabel>
            <Form.Control
              name="label"
              placeholder={t("Enter holiday name")}
              errorMessage={formError.label}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Holiday Type')}</Form.ControlLabel>
            <Form.Control
              name="type"
              accepter={SelectPicker}
              data={typeOptions}
              searchable={false}
              placeholder={t("Select type")}
              block
              errorMessage={formError.type}
              placement="auto"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Islamic Holiday')}</Form.ControlLabel>
            <Form.Control
              name="islamic"
              accepter={Toggle}
              checked={formValue.islamic}
              onChange={checked => handleToggleChange('islamic', checked)}
              checkedChildren={t("Yes")}
              unCheckedChildren={t("No")}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Recurrent Holiday')}</Form.ControlLabel>
            <Form.Control
              name="reccurent"
              accepter={Toggle}
              checked={formValue.reccurent}
              onChange={checked => handleToggleChange('reccurent', checked)}
              checkedChildren={t("Yes")}
              unCheckedChildren={t("No")}
            />
            <Form.HelpText>{t('Toggle on if this holiday repeats yearly')}</Form.HelpText>
          </Form.Group>

          {formValue.islamic && (
            <Form.Group>
              <Form.ControlLabel>{t('Days Per Year (Optional)')}</Form.ControlLabel>
              <Form.Control
                name="daysPerYear"
                accepter={InputNumber}
                min={0}
                placeholder={t("Enter days per year if applicable")}
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default AddHolidayDrawer;